import {
  Category,
  ValueType,
  PopulationDataType,
  PopulationData,
  SupportCategoryType,
  Format,
  Karte,
  SimilarityCategory,
  GeoData,
  GeoDataProperties,
  Values
} from './types';

export const getAreaName = (
  properties: GeoDataProperties,
  category: Category
) =>
  category === Category.Ku && properties.GST_NAME && properties.CSS_NAME
    ? properties.GST_NAME + properties.CSS_NAME
    : properties.area_name;

export const getValues = (
  data: GeoData | undefined,
  populationData: PopulationData[] | undefined,
  karte: Karte
): Values => {
  return (
    data?.features.reduce<Values>((v, f) => {
      const area = getAreaName(f.properties, karte.category);
      const value = getValue(populationData, karte, area);
      if (!area || typeof value !== 'number') return v;
      return {
        ...v,
        [area]: value
      };
    }, {}) ?? {}
  );
};

export const getValue = (
  populationData: PopulationData[] | undefined,
  karte: Karte,
  area?: string,
  dataType?: PopulationDataType
) => {
  if (!Array.isArray(populationData) || !karte.populationDataType) {
    return undefined;
  }
  const dt = dataType ?? karte.populationDataType;
  const areaName = area ?? karte.area;

  const filteredAreaData = populationData.filter(
    d => (karte.category === Category.Ku ? d.cityName : d.areaName) === areaName
  );

  if (karte.format === Format.Similarity) {
    const d = filteredAreaData.find(d => d.year === karte.year);
    return d ? getAmountData(d, PopulationDataType.Similarity) : undefined;
  }

  const amount = filteredAreaData
    .filter(d => d.year === karte.year)
    .reduce((a, b) => a + getAmountData(b, dt, karte.support), 0);

  switch (karte.valueType) {
    case ValueType.Amount:
      return amount;
    case ValueType.Proportion:
      const comparison = getOldestData(
        filteredAreaData,
        karte.category,
        areaName,
        dt
      );
      return Math.floor((amount / comparison) * 100);
  }
};

export function getAmountData(
  data: PopulationData,
  type: PopulationDataType,
  supportType?: SupportCategoryType
) {
  switch (type) {
    case PopulationDataType.Household:
      return data.setai;
    case PopulationDataType.Total:
      return data.all.total;
    case PopulationDataType.Less15:
      return data.all.less15;
    case PopulationDataType.Age15_65:
      return data.all.age15_65;
    case PopulationDataType.Over65:
      return data.all.over65;
    case PopulationDataType.Similarity:
      return data.similarity.similarClusterIndex;
    case PopulationDataType.Support: //データタイプが要介護だった場合は、さらに詳細なデータがあるためさらに条件分岐
      switch (supportType) {
        case SupportCategoryType.supportNeed_1:
          return data.support.supportNeed_1;
        case SupportCategoryType.supportNeed_2:
          return data.support.supportNeed_2;
        case SupportCategoryType.lSupportNeed_1:
          return data.support.lSupportNeed_1;
        case SupportCategoryType.lSupportNeed_2:
          return data.support.lSupportNeed_2;
        case SupportCategoryType.lSupportNeed_3:
          return data.support.lSupportNeed_3;
        case SupportCategoryType.lSupportNeed_4:
          return data.support.lSupportNeed_4;
        case SupportCategoryType.lSupportNeed_5:
          return data.support.lSupportNeed_5;
        default:
          return data.support.supportNeed_1;
      }
    /*
    case PopulationDataType.Over75:
      return data.all.over75;
    case PopulationDataType.Over85:
      return data.all.over85;
    case PopulationDataType.Male:
      return data.male.total;
    case PopulationDataType.Female:
      return data.female.total;
    case PopulationDataType.M0:
      return data.male.p0;
    case PopulationDataType.M5:
      return data.male.p5;
    case PopulationDataType.M10:
      return data.male.p10;
    case PopulationDataType.M15:
      return data.male.p15;
    case PopulationDataType.M20:
      return data.male.p20;
    case PopulationDataType.M25:
      return data.male.p25;
    case PopulationDataType.M30:
      return data.male.p30;
    case PopulationDataType.M35:
      return data.male.p35;
    case PopulationDataType.M40:
      return data.male.p40;
    case PopulationDataType.M45:
      return data.male.p45;
    case PopulationDataType.M50:
      return data.male.p50;
    case PopulationDataType.M55:
      return data.male.p55;
    case PopulationDataType.M60:
      return data.male.p60;
    case PopulationDataType.M65:
      return data.male.p65;
    case PopulationDataType.M70:
      return data.male.p70;
    case PopulationDataType.M75:
      return data.male.p75;
    case PopulationDataType.M80:
      return data.male.p80;
    case PopulationDataType.M85:
      return data.male.p85;
    case PopulationDataType.M90:
      return data.male.p90;
    case PopulationDataType.M95:
      return data.male.p95;
    case PopulationDataType.M100:
      return data.male.p100;
    case PopulationDataType.M_other:
      return data.male.other;
    case PopulationDataType.M_less15:
      return data.male.less15;
    case PopulationDataType.M_15_65:
      return data.male.age15_65;
    case PopulationDataType.M_over65:
      return data.male.over65;
    case PopulationDataType.M_over75:
      return data.male.over75;
    case PopulationDataType.M_over85:
      return data.male.over85;
    case PopulationDataType.F0:
      return data.female.p0;
    case PopulationDataType.F5:
      return data.female.p5;
    case PopulationDataType.F10:
      return data.female.p10;
    case PopulationDataType.F15:
      return data.female.p15;
    case PopulationDataType.F20:
      return data.female.p20;
    case PopulationDataType.F25:
      return data.female.p25;
    case PopulationDataType.F30:
      return data.female.p30;
    case PopulationDataType.F35:
      return data.female.p35;
    case PopulationDataType.F40:
      return data.female.p40;
    case PopulationDataType.F45:
      return data.female.p45;
    case PopulationDataType.F50:
      return data.female.p50;
    case PopulationDataType.F55:
      return data.female.p55;
    case PopulationDataType.F60:
      return data.female.p60;
    case PopulationDataType.F65:
      return data.female.p65;
    case PopulationDataType.F70:
      return data.female.p70;
    case PopulationDataType.F75:
      return data.female.p75;
    case PopulationDataType.F80:
      return data.female.p80;
    case PopulationDataType.F85:
      return data.female.p85;
    case PopulationDataType.F90:
      return data.female.p90;
    case PopulationDataType.F95:
      return data.female.p95;
    case PopulationDataType.F100:
      return data.female.p100;
    case PopulationDataType.F_other:
      return data.female.other;
    case PopulationDataType.F_less15:
      return data.female.less15;
    case PopulationDataType.F_15_65:
      return data.female.age15_65;
    case PopulationDataType.F_over65:
      return data.female.over65;
    case PopulationDataType.F_over75:
      return data.female.over75;
    case PopulationDataType.F_over85:
      return data.female.over85;
    */
  }
}

export function getOldestData(
  data: PopulationData[],
  category: Category,
  area: string,
  dataType: PopulationDataType
) {
  const years = Array.from(new Set(data.map(d => d.year)));
  return data
    .filter(
      d =>
        (category === Category.Ku ? d.cityName : d.areaName) === area &&
        d.year === years[0]
    )
    .reduce((a, b) => a + getAmountData(b, dataType), 0);
}

export const getRange = (
  category: Category,
  dataType: PopulationDataType,
  valueType: ValueType
): number[] => {
  if (valueType === ValueType.Proportion) {
    return dataType === PopulationDataType.Support
      ? [50, 100, 150, 200]
      : [50, 90, 110, 150];
  }
  if (category === Category.Chome) {
    switch (dataType) {
      case PopulationDataType.Household:
        return [500, 1000, 3000, 15000];
      case PopulationDataType.Total:
        return [1000, 2000, 5000, 20000];
      case PopulationDataType.Less15:
        return [300, 500, 1000, 5000];
      case PopulationDataType.Age15_65:
        return [1000, 2000, 5000, 20000];
      case PopulationDataType.Over65:
        return [500, 1000, 2000, 8000];
    }
  } else if (category === Category.Ku) {
    switch (dataType) {
      case PopulationDataType.Household:
        return [100000, 120000, 130000, 150000];
      case PopulationDataType.Total:
        return [200000, 220000, 250000, 300000];
      case PopulationDataType.Less15:
        return [10000, 20000, 25000, 30000];
      case PopulationDataType.Age15_65:
        return [100000, 120000, 150000, 200000];
      case PopulationDataType.Over65:
        return [20000, 30000, 40000, 50000];
    }
  } else {
    switch (dataType) {
      case PopulationDataType.Household:
        return [10000, 20000, 30000, 50000];
      case PopulationDataType.Total:
        return [20000, 50000, 80000, 100000];
      case PopulationDataType.Less15:
        return [5000, 10000, 20000, 30000];
      case PopulationDataType.Age15_65:
        return [20000, 50000, 80000, 100000];
      case PopulationDataType.Over65:
        return [10000, 30000, 50000, 80000];
      case PopulationDataType.Support:
        return [100, 150, 200, 250];
    }
  }
  return [0, 0, 0, 0];
};

export const getLegendColor = (num: number) => {
  switch (num) {
    case 4:
      return '#D1172D';
    case 3:
      return '#FC6A30';
    case 2:
      return '#FFA526';
    case 1:
      return '#FED46B';
    case 0:
      return '#FFEDA0';
  }
};

export const getSimilarityLegendColor = (num: number) => {
  switch (num) {
    case 0:
      return 'brown';
    case 1:
      return 'green';
    case 2:
      return 'red';
    case 3:
      return 'blue';
    case 4:
      return 'orange';
    case 5:
      return 'pink';
    case 6:
      return 'purple';
    case 7:
    case 98:
      return 'gray';
    // データ不足の場合99が渡ってくるため
    case 8:
    case 99:
      return 'black';
  }
};

export function getProportionData(
  data: PopulationData,
  type: PopulationDataType,
  comparison: number
) {
  const value = getAmountData(data, type);
  return Math.floor((value / comparison) * 100);
}

export const getLegendData = (
  category: Category,
  dataType: PopulationDataType | undefined,
  valueType: ValueType,
  format: Format
) => {
  if (!dataType) return undefined;
  const range = getRange(category, dataType, valueType);
  // フォーマットがSimilarityの場合に、mapConfigに配列のインデックス番号を渡し、色を取得
  if (format === Format.Similarity) {
    return Object.values(SimilarityCategory).map((r, i) => ({
      color: getSimilarityLegendColor(i),
      text: r
    }));
  }

  return [
    ...range.map((r, i) => ({
      color: getLegendColor(i),
      text: (range[i - 1] ?? '') + '~' + r
    })),
    {
      color: getLegendColor(4),
      text: range[3] + '~'
    }
  ].reverse();
};
